// @ts-nocheck -- placeholder art, ported as-is from the Neon Kabuki Gunsmith page (sil.js).
// Pixel side views of a build, drawn from each gun's and part's `draw` block in common/data/gunsmith.json.
// The menus now use client/src/weapon-art.ts (hand-shaded); this file keeps the geometry (shapesFor) and the old drawing as a fallback.
import { GUNS, isModular, part, resolve } from '../../common/src/gunsmith';
import type { Item } from '../../common/src/rules';
// Pixel-art side views of a build, drawn in centimetres and rasterised with a 1 px dark outline
// (the game's own sprite style). Geometry comes from each weapon's `draw` block and each part's `draw`.

  var COL = {
    steel: [138, 145, 168], dark: [86, 90, 112], poly: [70, 72, 92], wood: [170, 108, 62], can: [100, 104, 124],
    rail: [118, 124, 146], glass: [111, 211, 255], tech: [45, 226, 200], glow: [255, 62, 165], tan: [150, 132, 96],
  };
  var OUTLINE = [8, 6, 14];

export function shapesFor(pl, r) {
    var d = pl.draw, R = d.R, H = d.H, bore = d.bore;
    var S = [];
    function rect(x, y, w, h, c, z) { S.push({ t: 'r', x: x, y: y, w: w, h: h, c: c, z: z == null ? 5 : z }); }
    function poly(pts, c, z) { S.push({ t: 'p', pts: pts, c: c, z: z == null ? 5 : z }); }
    function circ(x, y, rad, c, z) { S.push({ t: 'c', x: x, y: y, r: rad, c: c, z: z == null ? 5 : z }); }
    var at = {};
    r.nodes.forEach(function (n) { if (n.id) at[n.path] = n.part || part(n.id); });
    function P(path) { return at[path] || null; }
    function dr(p) { return (p && p.draw) || {}; }

    var muzzleX = R, optY = H, optX = d.top;
    var sy0 = d.sy ? d.sy[0] : 1, sy1 = d.sy ? d.sy[1] : H - 0.6;
    var hg = null; // handguard box for accessory anchors

    function integratedGrip(c) {
      var g = d.grip, gx = g[0], gl = g[1], ga = g[2] * Math.PI / 180, dx = Math.tan(ga) * gl;
      poly([[gx + 3.6, 0.4], [gx, 0.4], [gx - dx, -gl], [gx - dx + 3.4, -gl]], c || 'poly', 4);
      rect(gx + 3.6, -1.5, 3.4, 0.5, 'poly', 4); rect(gx + 6.6, -1.5, 0.5, 1.7, 'poly', 4); // trigger guard
      return { x: gx - dx, y: -gl };
    }
    function gripPart(p) {
      var a = (dr(p).a == null ? 18 : dr(p).a) * Math.PI / 180, gl = 9, dx = Math.tan(a) * gl, gx = d.gx;
      poly([[gx + 3.4, 0.2], [gx, 0.2], [gx - dx, -gl], [gx - dx + 3.2, -gl]], dr(p).m === 'wood' ? 'wood' : 'poly', 4);
      rect(gx + 3.6, -1.5, 3.6, 0.5, 'dark', 4); rect(gx + 6.8, -1.5, 0.5, 1.7, 'dark', 4);
    }
    function magPart(p, mx) {
      var m = dr(p), l = m.l || 10;
      if (m.s === 'curved') {
        var w = 3.5, c = m.c || 3;
        poly([[mx, 0.2], [mx + w, 0.2], [mx + w + c * 0.4, -l * 0.5], [mx + w + c, -l], [mx + c, -l], [mx + c * 0.4 - 0.3, -l * 0.5]], 'dark', 3);
      } else if (m.s === 'drum') {
        rect(mx + 0.4, -3, 3, 3.2, 'dark', 3); circ(mx + 1.9, -3 - l / 2, l / 2, 'dark', 3); circ(mx + 1.9, -3 - l / 2, l / 6, 'steel', 3);
      } else if (m.s === 'cell') {
        rect(mx, -l, 5, l + 0.2, 'dark', 3); rect(mx + 0.9, -l + 1, 0.7, Math.max(1, l - 2), 'tech', 3);
      } else if (m.s === 'box' || m.s === 'quadmag') {
        rect(mx, -l, m.s === 'quadmag' ? 4.8 : 4.2, l + 0.2, 'dark', 3);
      } else {
        rect(mx, -l, 3.2, l + 0.2, 'dark', 3);
      }
    }
    function stockPart(p, x0) {
      var m = dr(p), l = m.l || 20, c = m.m === 'wood' ? 'wood' : 'poly';
      var yc = (sy0 + sy1) / 2;
      if (m.s === 'solid') {
        poly([[x0, sy1], [x0 - l, sy1 + 0.4], [x0 - l, sy0 - 3.6], [x0 - l * 0.3, sy0 - 0.6], [x0, sy0]], c, 2);
        rect(x0 - l - 0.7, sy0 - 3.6, 0.9, sy1 - sy0 + 4, 'dark', 2);
      } else if (m.s === 'shotstock') {
        poly([[x0, sy1], [x0 - l, sy1 - 0.8], [x0 - l, sy0 - 5], [x0 - l * 0.45, sy0 - 2.4], [x0 - 3, sy0 - 0.8], [x0, sy0]], c, 2);
        rect(x0 - l - 0.7, sy0 - 5, 0.9, sy1 - sy0 + 4.2, 'dark', 2);
      } else if (m.s === 'chassis') {
        poly([[x0, sy1], [x0 - l, sy1], [x0 - l, sy0 - 4.2], [x0 - l * 0.55, sy0 - 4.2], [x0 - l * 0.4, sy0 - 1.2], [x0, sy0 - 0.4]], 'dark', 2);
        rect(x0 - l * 0.8, sy1, l * 0.45, 1.2, 'dark', 2); rect(x0 - l * 0.65, sy0 - 2.4, l * 0.2, 1.6, 'steel', 3);
        rect(x0 - l - 0.8, sy0 - 4.2, 1.0, sy1 - sy0 + 4.4, 'poly', 2);
      } else if (m.s === 'folder' || m.s === 'skeleton' || m.s === 'wire') {
        var th = m.s === 'wire' ? 0.5 : 0.85, cc = m.s === 'wire' ? 'steel' : c;
        rect(x0 - l, sy1 - th, l, th, cc, 2);
        poly([[x0, sy0], [x0, sy0 + th], [x0 - l, sy0 - 2.6 + th], [x0 - l, sy0 - 2.6]], cc, 2);
        if (m.s === 'skeleton') poly([[x0 - l * 0.45, sy1 - th], [x0 - l * 0.45 + th, sy1 - th], [x0 - l * 0.6 + th, sy0 - 1.4], [x0 - l * 0.6, sy0 - 1.4]], cc, 2);
        rect(x0 - l - 0.9, sy0 - 3, 1.1, sy1 - sy0 + 3, 'dark', 2);
      } else if (m.s === 'pgonly') {
        poly([[x0 + 2.2, sy0 + 0.2], [x0 - 0.2, sy0 + 0.2], [x0 - 3.6, sy0 - 7.8], [x0 - 0.6, sy0 - 8.6]], 'poly', 2);
      } else if (m.s === 'adapter') {
        rect(x0 - 4, sy0, 4, sy1 - sy0, 'dark', 2);
        rect(x0 - 4 - 17, yc - 1.3, 17, 2.6, 'steel', 1);
        var inner = P(currentStockPath + '/stock');
        if (inner) arStock(inner, x0 - 4, 17);
      } else {
        arStock(p, x0, 17);
      }
    }
    function arStock(p, x0, tubeL) {
      var m = dr(p), l = m.l || 20, yc = (sy0 + sy1) / 2;
      var rear = x0 - (tubeL + l - 17);
      var body = m.s === 'prec' ? 14 : m.s === 'mini' ? 7 : 11;
      var h = m.s === 'prec' ? 6.4 : m.s === 'mini' ? 4.6 : 5.8;
      poly([[rear + body, yc + h * 0.42], [rear, yc + h * 0.52], [rear, yc - h * 0.5], [rear + body * 0.6, yc - h * 0.42], [rear + body, yc - 1.2]], 'poly', 2);
      if (m.s === 'prec') rect(rear + 2, yc + h * 0.5, body - 4, 1.4, 'poly', 2);
      rect(rear - 0.8, yc - h * 0.5, 1.0, h * 1.02, 'dark', 2);
    }
    var currentStockPath = 'stock';

    // ---------------------------------------------------------------- receiver by body
    var gripBottom = null;
    if (d.body === 'pistol') {
      rect(1.6, 0.2, R - 3.4, H * 0.5, 'poly', 4);
      rect(0, H * 0.45, R, H * 0.55, 'dark', 6);
      rect(R * 0.15, H - 0.35, R * 0.3, 0.35, 'steel', 7);
      gripBottom = integratedGrip('poly');
      optY = H;
    } else if (d.body === 'smg') {
      rect(0, 0.6, R, H - 0.6, 'steel', 5); rect(2, H - 0.2, R - 6, 0.6, 'dark', 5);
      rect(R - 1, bore + 1.4, 12, 1.0, 'steel', 4); // cocking tube
      integratedGrip('poly'); rect(d.mx - 0.3, -0.6, 4.2, 1.2, 'steel', 5);
      optY = H + 0.4;
    } else if (d.body === 'mac') {
      rect(0, 0, R, H, 'dark', 5); rect(0.6, H - 0.9, R - 1.2, 0.9, 'steel', 6);
      gripBottom = integratedGrip('poly');
      rect(R, bore - 0.7, d.stub, 1.4, 'steel', 4); muzzleX = R + d.stub;
      optY = H;
    } else if (d.body === 'smart') {
      poly([[0, 0.6], [R - 3, 0], [R, 1.6], [R, H - 1.2], [R - 4, H], [0, H]], 'dark', 5);
      rect(1.5, H * 0.46, R - 7, 0.55, 'tech', 7);
      rect(4, H, R - 10, 0.6, 'rail', 6);
      integratedGrip('poly');
      rect(R, bore - 0.6, d.stub, 1.2, 'steel', 4); muzzleX = R + d.stub;
      optY = H + 0.6;
    } else if (d.body === 'ak') {
      rect(0, 0, R, H * 0.82, 'steel', 5);
      rect(d.mx + 3.8, -1.5, 3.2, 0.5, 'steel', 4); rect(d.mx + 6.7, -1.5, 0.5, 1.7, 'steel', 4);
      optY = H;
    } else if (d.body === 'ar') {
      rect(0, 0, R, H * 0.52, 'dark', 5); rect(0, H * 0.52, R, H * 0.48, 'poly', 5);
      rect(0.6, H, R - 1.2, 0.7, 'rail', 6);
      rect(d.mx - 0.4, -1.4, 4.2, 1.6, 'dark', 5);
      optY = H + 0.7;
    } else if (d.body === 'bullpup') {
      poly([[0, 0], [R * 0.42, 0], [R * 0.5, -0.5], [R, 0.3], [R, H - 2], [R - 6, H - 1.3], [8, H - 1.3], [0, H - 2.4]], 'poly', 5);
      rect(-1, 0, 1.2, H - 2.4, 'dark', 5);
      rect(R * 0.36, H - 1.3, R * 0.52, 0.9, 'rail', 6);
      integratedGrip('poly');
      optY = H - 0.4;
    } else if (d.body === 'pump') {
      rect(0, 0, R, H, 'dark', 5); rect(0.8, H - 0.6, R - 1.6, 0.6, 'steel', 6);
      rect(3, -1.4, 4, 0.5, 'dark', 4); rect(6.5, -1.4, 0.5, 1.6, 'dark', 4);
      var ext = P('ext'), tl = d.tube + (ext ? dr(ext).l || 0 : 0);
      rect(R, bore - 3.3, tl, 1.9, 'dark', 3); rect(R + tl - 0.6, bore - 3.5, 0.8, 2.3, 'steel', 3);
      optY = H;
    } else if (d.body === 'chassis') {
      rect(0, 0, R, H, 'dark', 5); rect(3, H, R - 5, 0.8, 'rail', 6);
      rect(R * 0.6, H * 0.6, 3.8, 0.9, 'steel', 7); circ(R * 0.6 + 4.2, H * 0.62, 0.9, 'steel', 7);
      rect(d.mx + 4.6, -1.5, 3.4, 0.5, 'dark', 4); rect(d.mx + 7.6, -1.5, 0.5, 1.7, 'dark', 4);
      rect(R, bore - 2.6, d.fe, 4.6, 'dark', 5);
      for (var k = 0; k < 5; k++) rect(R + 3 + k * (d.fe - 6) / 4, bore - 0.4, 1.6, 0.8, 'poly', 6);
      optY = H + 0.8;
    } else if (d.body === 'coil') {
      poly([[0, 0], [R, 0], [R, H], [6, H], [0, H - 2]], 'dark', 5);
      rect(2, H * 0.58, R - 5, 0.6, 'tech', 7);
      rect(6, H, R - 12, 0.6, 'rail', 6);
      integratedGrip('poly');
      optY = H + 0.6;
    }

    // ---------------------------------------------------------------- barrel, handguard, muzzle chain
    var bar = P('barrel');
    if (bar) {
      var b = dr(bar), bl = b.l || 0, bt = b.t || 1.2;
      if (b.s === 'pbar') { if (bl > 0) rect(R, bore - 0.45, bl, 0.9, 'steel', 6); muzzleX = R + bl; }
      else if (b.s === 'sdbar') { rect(R, bore - (b.d || 3.4) / 2, bl, b.d || 3.4, 'can', 4); muzzleX = R + bl; }
      else if (b.s === 'coil') {
        rect(R, bore + 0.6, bl, 0.9, 'steel', 4); rect(R, bore - 1.5, bl, 0.9, 'steel', 4);
        for (var q = R + 3; q < R + bl - 2; q += 6) rect(q, bore - 1.9, 1.3, 3.3, 'tech', 5);
        muzzleX = R + bl;
      } else {
        rect(R, bore - bt / 2, bl, bt, 'steel', 3);
        if (d.body === 'ak') rect(R + bl - 4, bore + bt / 2, 1.2, 1.8, 'steel', 3);
        muzzleX = R + bl;
      }
    }
    var hgp = P('handguard');
    if (hgp) {
      var h = dr(hgp), hl = h.l || 16, hh = h.h || 4;
      var x0 = d.body === 'pump' ? R + 2 : R, yc = d.body === 'pump' ? bore - 2.4 : bore;
      var hc = { wood: 'wood', poly: 'poly', slim: 'poly', quad: 'dark', slot: 'dark', pump: 'poly', pumprail: 'dark' }[h.s] || 'poly';
      if (d.body === 'ak' && (h.s === 'wood' || h.s === 'poly')) {
        rect(x0, yc - hh * 0.62, hl, hh * 0.62, hc, 6);
        rect(x0 + 1, yc + 0.5, hl * 0.72, hh * 0.38, hc, 6);
        rect(x0, yc + 0.4, hl + 3, 1.0, 'steel', 5); // gas tube
      } else {
        rect(x0, yc - hh / 2, hl, hh, hc, 6);
      }
      if (h.s === 'quad' || h.s === 'pumprail') { for (var t = x0 + 1; t < x0 + hl - 1; t += 2) { rect(t, yc + hh / 2, 1, 0.4, 'rail', 7); rect(t, yc - hh / 2 - 0.4, 1, 0.4, 'rail', 7); } }
      if (h.s === 'slot') { for (var u = x0 + 2; u < x0 + hl - 2; u += 3.2) rect(u, yc - 0.4, 1.8, 0.8, 'poly', 7); }
      if (h.s === 'wood') rect(x0 + 2, yc - hh * 0.3, hl - 4, 0.3, 'tan', 7);
      hg = { x0: x0, x1: x0 + hl, yb: yc - hh / 2 - (h.s === 'quad' ? 0.4 : 0) };
      if (d.body === 'ak' && (h.s === 'wood' || h.s === 'poly')) hg.yb = yc - hh * 0.62;
    }
    // platform-level muzzle (S-10) or barrel-provided muzzle
    var muz = P('barrel/muzzle') || P('muzzle');
    var muzPath = P('barrel/muzzle') ? 'barrel/muzzle' : 'muzzle';
    if (muz) {
      var m = dr(muz), ml = m.l || 4, md = m.d || 2.4;
      if (muz.type === 'suppressor') {
        rect(muzzleX, bore - md / 2, ml, md, 'can', 6); rect(muzzleX + ml - 0.6, bore - md / 2, 0.6, md, 'dark', 6);
      } else {
        var mc = m.s === 'choke' ? 'dark' : m.s === 'focus' ? 'dark' : 'steel';
        rect(muzzleX, bore - md / 2, ml, md, mc, 6);
        if (m.s === 'brake') { rect(muzzleX + ml * 0.3, bore + md / 2 - 0.5, 0.6, 0.5, 'dark', 7); rect(muzzleX + ml * 0.65, bore + md / 2 - 0.5, 0.6, 0.5, 'dark', 7); }
        if (m.s === 'flash') { for (var f = muzzleX + 1; f < muzzleX + ml - 0.5; f += 1.4) rect(f, bore - 0.3, 0.6, 0.6, 'dark', 7); }
        if (m.s === 'focus') rect(muzzleX + ml - 1.4, bore - md / 2, 0.7, md, 'tech', 7);
        var sp = P(muzPath + '/supp');
        if (sp) { var s2 = dr(sp), sl = s2.l || 14, sd = s2.d || 3.4; rect(muzzleX + ml - 2, bore - sd / 2, sl + 2, sd, 'can', 7); rect(muzzleX + ml + sl - 0.6, bore - sd / 2, 0.6, sd, 'dark', 7); }
      }
    }

    // ---------------------------------------------------------------- dust cover, mounts, optics
    var dc = P('dustcover');
    if (dc) {
      rect(0.4, H * 0.82, R - 1.8, H * 0.18 + 0.4, 'steel', 6);
      optY = H + 0.4;
      if (dr(dc).s === 'railcover') { rect(R * 0.22, H + 0.4, R * 0.62, 0.7, 'rail', 7); optY = H + 1.1; }
    }
    function drawOptic(p, ox, oy) {
      var o = dr(p), l = o.l || 6, h = o.h || 4;
      if (o.s === 'dot') { rect(ox - l / 2, oy, l, h, 'dark', 8); rect(ox - l / 2 + 0.5, oy + 0.4, 0.7, h - 0.6, 'glass', 9); }
      else if (o.s === 'reflex') { rect(ox - l / 2, oy, l, 0.9, 'dark', 8); rect(ox + l / 2 - 1.3, oy + 0.9, 1.1, h - 0.9, 'dark', 8); rect(ox + l / 2 - 1.1, oy + 1.2, 0.5, h - 1.6, 'glass', 9); }
      else if (o.s === 'holo') { rect(ox - l / 2, oy, l, h, 'dark', 8); rect(ox - l / 2 + 1.4, oy + 1.4, l - 3.8, h - 2.2, 'glass', 9); }
      else if (o.s === 'prism') { rect(ox - l / 2, oy, l, h * 0.8, 'dark', 8); rect(ox - l / 2 - 1.3, oy + 0.7, 1.5, h * 0.55, 'dark', 8); rect(ox + l / 2 - 0.7, oy + 0.9, 0.6, h * 0.45, 'glass', 9); }
      else if (o.s === 'thermal') { rect(ox - l / 2, oy, l, h, 'poly', 8); circ(ox + l / 2 - 0.4, oy + h / 2, h * 0.42, 'dark', 9); circ(ox + l / 2 - 0.4, oy + h / 2, h * 0.18, 'glow', 9); }
      else if (o.s === 'link') { rect(ox - l / 2, oy, l, h, 'dark', 8); rect(ox - l / 2 + 1, oy + h - 1.2, l - 2, 0.6, 'glow', 9); rect(ox + l / 2 - 0.6, oy + 1, 0.6, h - 2, 'glass', 9); }
      else if (o.s === 'scope') {
        var cy = oy + h / 2;
        rect(ox - l / 2, cy - h * 0.28, l, h * 0.56, 'dark', 8);
        rect(ox + l / 2 - l * 0.26, cy - h / 2, l * 0.26, h, 'dark', 8);
        rect(ox - l / 2, cy - h * 0.42, l * 0.16, h * 0.84, 'dark', 8);
        rect(ox - 1.3, cy + h * 0.28, 2.6, 1.2, 'dark', 8);
        rect(ox + l / 2 - 0.5, cy - h * 0.34, 0.5, h * 0.68, 'glass', 9);
      }
    }
    function drawOpticSlot(path, ox, oy) {
      var p = P(path); if (!p) return;
      if (p.type === 'mount') {
        var mm = dr(p), ml2 = mm.l || 12, mh = mm.h || 2;
        if (mm.s === 'rings') {
          rect(ox - ml2 / 2, oy, ml2, 0.7, 'dark', 8);
          rect(ox - ml2 / 2 + 1.4, oy, 1.4, mh + 1.2, 'dark', 8); rect(ox + ml2 / 2 - 2.8, oy, 1.4, mh + 1.2, 'dark', 8);
          var sc = P(path + '/scope'); if (sc) drawOptic(sc, ox, oy + mh - 0.4);
        } else {
          rect(ox - ml2 / 2, oy, ml2, mh, mm.s === 'claw' ? 'steel' : 'rail', 7);
          drawOpticSlot(path + '/optic', ox, oy + mh);
        }
      } else drawOptic(p, ox, oy);
    }
    if (P('dustcover/optic')) drawOpticSlot('dustcover/optic', optX, optY);
    if (P('optic')) drawOpticSlot('optic', optX, optY);
    if (P('mount')) {
      var mp = P('mount'), md2 = dr(mp);
      rect(optX - (md2.l || 12) / 2, optY, md2.l || 12, md2.h || 1.4, md2.s === 'claw' ? 'steel' : 'rail', 7);
      drawOpticSlot('mount/optic', optX, optY + (md2.h || 1.4));
    }

    // ---------------------------------------------------------------- grips, magazines
    if (P('grip')) gripPart(P('grip'));
    var mag = P('mag');
    if (mag) {
      var mdd = dr(mag);
      if (mdd.s === 'pmag' || mdd.s === 'gmag') {
        if (gripBottom && mdd.l > 0) rect(gripBottom.x + 0.5, gripBottom.y - mdd.l, 2.8, mdd.l + 0.3, 'dark', 3);
      } else magPart(mag, d.mx);
    }

    // ---------------------------------------------------------------- stocks
    var st = P('stock'), tube = P('tube');
    if (tube) {
      var tl2 = dr(tube).l || 17, yc2 = (sy0 + sy1) / 2;
      rect(-tl2, yc2 - 1.3, tl2, 2.6, 'steel', 1);
      var tst = P('tube/stock'); if (tst) arStock(tst, 0, tl2);
    }
    if (st) {
      if (st.type === 'tube') {
        var tl3 = dr(st).l || 17, yc3 = (sy0 + sy1) / 2;
        rect(-tl3, yc3 - 1.3, tl3, 2.6, 'steel', 1);
        var ss = P('stock/stock'); if (ss) arStock(ss, 0, tl3);
      } else { currentStockPath = 'stock'; stockPart(st, 0); }
    }

    // ---------------------------------------------------------------- accessories
    function drawFore(p, x, y) {
      var s = dr(p).s;
      if (s === 'vert') rect(x - 1.1, y - 6, 2.2, 6, 'poly', 8);
      else if (s === 'angled') poly([[x - 3.6, y], [x + 1.6, y], [x - 1, y - 3.2], [x - 2.7, y - 3.2]], 'poly', 8);
      else if (s === 'stop') rect(x - 0.7, y - 1.7, 1.4, 1.7, 'poly', 8);
      else if (s === 'gyro') { rect(x - 1.3, y - 6.4, 2.6, 6.4, 'dark', 8); rect(x - 1.3, y - 4, 2.6, 0.7, 'tech', 9); }
      else if (s === 'strap') { rect(x - 1.6, y - 0.4, 3.2, 0.4, 'tan', 8); rect(x - 1.6, y - 2.4, 0.5, 2.4, 'tan', 8); rect(x + 1.1, y - 2.4, 0.5, 2.4, 'tan', 8); rect(x - 1.6, y - 2.6, 3.2, 0.5, 'tan', 8); }
    }
    function drawTac(p, x, y) {
      var s = dr(p).s;
      if (s === 'light') { rect(x - 2.6, y - 1.9, 5.2, 1.9, 'dark', 8); rect(x + 2.6, y - 1.9, 0.5, 1.9, 'glass', 9); }
      else if (s === 'laser') { rect(x - 1.6, y - 1.5, 3.2, 1.5, 'dark', 8); rect(x + 1.6, y - 1.0, 0.6, 0.6, 'glow', 9); }
      else { rect(x - 2.6, y - 2.2, 5.2, 2.2, 'dark', 8); rect(x + 2.6, y - 2.0, 0.5, 1.0, 'glass', 9); rect(x + 2.6, y - 0.9, 0.5, 0.5, 'glow', 9); }
    }
    function drawAcc(p, x, y) { if (!p) return; if (p.type === 'foregrip') drawFore(p, x, y); else drawTac(p, x, y); }
    function drawRailSlot(path, x, y) { if (!P(path)) return; rect(x - 1.7, y - 0.6, 3.4, 0.6, 'rail', 8); drawAcc(P(path + '/acc'), x, y - 0.6); }

    if (hg) {
      drawAcc(P('handguard/foregrip'), hg.x0 + (hg.x1 - hg.x0) * 0.6, hg.yb);
      drawAcc(P('handguard/tactical'), hg.x0 + (hg.x1 - hg.x0) * 0.86, hg.yb);
      drawRailSlot('handguard/railA', hg.x0 + (hg.x1 - hg.x0) * 0.55, hg.yb);
      drawRailSlot('handguard/railB', hg.x0 + (hg.x1 - hg.x0) * 0.86, hg.yb);
      drawRailSlot('handguard/railC', hg.x0 + (hg.x1 - hg.x0) * 0.28, hg.yb);
    }
    if (d.body === 'chassis') {
      drawRailSlot('railA', R + d.fe * 0.55, bore - 2.6);
      drawRailSlot('railB', R + d.fe * 0.86, bore - 2.6);
    }
    if (d.fgx != null) drawAcc(P('foregrip'), d.fgx, d.body === 'coil' ? bore - 1.9 : 0.2);
    if (d.body === 'mac' && P('foregrip')) drawFore(P('foregrip'), R + d.stub * 0.5, bore - 0.7);
    if (d.tac != null) drawAcc(P('tactical'), d.tac, d.body === 'pistol' ? 0.2 : d.body === 'bullpup' ? 3.4 : 0.6);
    if (P('module')) { rect(R * 0.5, 1.4, 8, 2.8, 'poly', 7); rect(R * 0.5 + 0.8, 2.2, 6.4, 1.2, 'tech', 8); }
    return S;
  }

  function bbox(s) {
    if (s.t === 'r') return [s.x, s.y, s.x + s.w, s.y + s.h];
    if (s.t === 'c') return [s.x - s.r, s.y - s.r, s.x + s.r, s.y + s.r];
    var xs = s.pts.map(function (p) { return p[0]; }), ys = s.pts.map(function (p) { return p[1]; });
    return [Math.min.apply(null, xs), Math.min.apply(null, ys), Math.max.apply(null, xs), Math.max.apply(null, ys)];
  }

  // Draw into a canvas; returns geometry so a DOM ruler can be placed under it.
  function draw(cv, pl, r) {
    var shapes = shapesFor(pl, r);
    var x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
    shapes.forEach(function (s) { var b = bbox(s); x0 = Math.min(x0, b[0]); y0 = Math.min(y0, b[1]); x1 = Math.max(x1, b[2]); y1 = Math.max(y1, b[3]); });
    var W = cv.width, Hh = cv.height, pad = 2;
    var sc = Math.min((W - pad * 2) / (x1 - x0), (Hh - pad * 2) / (y1 - y0));
    var offX = (W - (x1 - x0) * sc) / 2, offY = (Hh - (y1 - y0) * sc) / 2;
    function X(x) { return offX + (x - x0) * sc; }
    function Y(y) { return Hh - (offY + (y - y0) * sc); }
    var g = cv.getContext('2d');
    g.clearRect(0, 0, W, Hh);
    shapes.slice().sort(function (a, b) { return a.z - b.z; }).forEach(function (s) {
      var c = COL[s.c] || COL.dark; g.fillStyle = 'rgb(' + c.join(',') + ')';
      if (s.t === 'r') {
        var l = Math.round(X(s.x)), rr = Math.round(X(s.x + s.w)), tp = Math.round(Y(s.y + s.h)), bt = Math.round(Y(s.y));
        g.fillRect(l, tp, Math.max(1, rr - l), Math.max(1, bt - tp));
      } else if (s.t === 'c') {
        g.beginPath(); g.arc(X(s.x), Y(s.y), Math.max(0.8, s.r * sc), 0, Math.PI * 2); g.fill();
      } else {
        g.beginPath(); s.pts.forEach(function (p, i) { if (i) g.lineTo(X(p[0]), Y(p[1])); else g.moveTo(X(p[0]), Y(p[1])); }); g.closePath(); g.fill();
      }
    });
    // crisp pixels, top highlight, 1 px dark outline
    var img = g.getImageData(0, 0, W, Hh), a = img.data, n = W * Hh, solid = new Uint8Array(n);
    for (var i = 0; i < n; i++) { if (a[i * 4 + 3] >= 110) { a[i * 4 + 3] = 255; solid[i] = 1; } else a[i * 4 + 3] = 0; }
    for (var y = 0; y < Hh; y++) for (var x = 0; x < W; x++) {
      var k = y * W + x;
      if (solid[k]) {
        if (y > 0 && !solid[k - W]) { a[k * 4] = Math.min(255, a[k * 4] + 34); a[k * 4 + 1] = Math.min(255, a[k * 4 + 1] + 34); a[k * 4 + 2] = Math.min(255, a[k * 4 + 2] + 34); }
      } else if ((x > 0 && solid[k - 1]) || (x < W - 1 && solid[k + 1]) || (y > 0 && solid[k - W]) || (y < Hh - 1 && solid[k + W])) {
        a[k * 4] = OUTLINE[0]; a[k * 4 + 1] = OUTLINE[1]; a[k * 4 + 2] = OUTLINE[2]; a[k * 4 + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return { x0: x0, x1: x1, sc: sc, offX: offX, W: W };
  }


/** Draw the item's gun into a canvas (modular guns only; returns false for the original three). */
export function legacyWeaponArt(cv: HTMLCanvasElement, item: Item | null | undefined): boolean {
  if (!item || !isModular(item.w)) return false;
  const pl = GUNS[item.w]; draw(cv, pl, resolve(pl, item.b ?? pl.defaults)); return true;
}
